import React from 'react'

import { ColorFormat } from 'modules/shares/types'
import ArtboardOverview from '../ArtboardOverview'
import LayoutSettings from '../LayoutSettings'
import GridSettings from '../GridSettings'
import Overview from '../Overview'
import Exports from '../Exports'
import { Box } from '@sketch/components'
import { EditableComponentDescription } from 'modules/shares/ComponentsWebView/components/ComponentDescription'

import { useComponentsDescriptionContext } from 'modules/shares/ComponentsWebView/context'

import { InspectorData } from 'modules/shares/Inspector/types'
import { VersionFragment, useEditDescriptionsMutation } from '@sketch/gql-types'
import { useEventDispatch } from '@sketch/utils'

declare module '@sketch/utils' {
  export interface EventsMap {
    versionIsTriggeredFromSymbol: string
  }
}

// Types
interface ArtboardInfoProps {
  artboardName: string
  isSymbol?: boolean
  symbolDocumentName?: string
  inspectorData: InspectorData
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  currentVersion: VersionFragment
  hasArtboardExportableAssets?: boolean
}

const ArtboardInfo: React.FC<ArtboardInfoProps> = ({
  artboardName,
  isSymbol,
  symbolDocumentName,
  inspectorData,
  colorFormat,
  onColorFormatChange,
  currentVersion,
  hasArtboardExportableAssets,
}) => {
  const {
    updateDescripton,
    componentsDescription: { selected, descriptions },
  } = useComponentsDescriptionContext()

  const onVersionTriggered = useEventDispatch('versionIsTriggeredFromSymbol')

  const [editDescription] = useEditDescriptionsMutation({
    onCompleted: () => {
      onVersionTriggered(descriptions[selected!].shareIdentifier!)
    },
    onError: 'show-toast',
  })

  const isArtboard = !inspectorData.isPage && inspectorData.frame

  return (
    <>
      <Overview
        artboardName={artboardName}
        inspectorData={inspectorData}
        isSymbol={isSymbol}
        symbolDocumentName={symbolDocumentName}
      />

      {isSymbol && (
        <EditableComponentDescription
          description={descriptions?.[selected || '']?.description ?? undefined}
          disabled={
            !descriptions?.[selected || '']?.canEditDescriptions ?? true
          }
          onEdit={description => {
            const componentSelected = selected!
            const component = descriptions[componentSelected]

            editDescription({
              variables: {
                baseVersionIdentifier: component.baseVersionIdentifier!,
                compatibilityVersion: component.compatibilityVersion!,
                documentVersion: component.documentVersion!,
                shareIdentifier: component.shareIdentifier!,
                edits: [
                  {
                    componentUuid: componentSelected,
                    description,
                  },
                ],
              },
            })

            updateDescripton(componentSelected, description)
          }}
        />
      )}

      {isArtboard && (
        <ArtboardOverview
          preset={inspectorData.preset}
          frame={inspectorData.frame}
          color={inspectorData.background}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
        />
      )}

      <LayoutSettings layout={{ ...inspectorData.layout }} />
      <GridSettings grid={inspectorData?.grid} />

      {isArtboard && (
        <Box marginTop="auto">
          <Exports
            for={{ type: 'artboard', value: inspectorData }}
            hasArtboardExportableAssets={hasArtboardExportableAssets}
            currentVersion={currentVersion}
          />
        </Box>
      )}
    </>
  )
}

export default ArtboardInfo
